import React, { FC, memo, useCallback, useContext, useEffect } from 'react'

import { useMutation } from '@apollo/client'
import { useFormContext } from 'react-hook-form'

import { AppContext, CALC_SHIPPING_COST, OTHER } from 'Const'
import { useCustomer } from 'Hooks/checkout'
import AddressFields, { FIELDS } from 'Pages/Checkout/AddressFields'
import { ShopCartFulfillmentAddress } from 'Types'
import { isShipping } from 'Utils/cartFulfillment'
import { customerAddressTitle } from 'Utils/customer'

interface Props {
  cssPrefix: string
  nameGroupPrefix: string
}

const FulfillmentAddress: FC<Props> = ({ cssPrefix, nameGroupPrefix }) => {
  const { cart } = useContext(AppContext)
  const { register, setValue, getValues } = useFormContext()
  const customer = useCustomer()
  const [calcShippingCost] = useMutation(CALC_SHIPPING_COST)
  const fulfillment = cart.fulfillment
  const primaryAddressId = customer?.primaryAddress?.id
  const customerAddresses = customer?.addresses

  const calcTransportCost = useCallback(() => {
    if (isShipping(fulfillment)) {
      void calcShippingCost({ variables: { address: getValues().shippingAddress } })
    }
  }, [calcShippingCost, fulfillment, getValues])

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target
      if (value === OTHER) {
        FIELDS.map((field) => setValue(`${nameGroupPrefix}[${field}]`, null))
      } else {
        const address = customerAddresses?.find(({ id }) => id === value)
        if (!address) return

        calcTransportCost()
        FIELDS.map((field) => setValue(`${nameGroupPrefix}[${field}]`, address[field]), {
          shouldDirty: true,
        })
      }
    },
    [calcTransportCost, nameGroupPrefix, setValue, customerAddresses],
  )

  useEffect(() => {
    let currentAddress: Partial<ShopCartFulfillmentAddress> | undefined
    if (fulfillment?.address) {
      currentAddress = customerAddresses?.find(
        ({ cityGooglePlaceId }) =>
          fulfillment.address?.cityGooglePlaceId && cityGooglePlaceId === fulfillment.address?.cityGooglePlaceId,
      )
      if (!currentAddress) {
        currentAddress = {
          ...fulfillment.address,
          firstName: fulfillment.address.firstName || customer?.firstName,
          lastName: fulfillment.address.lastName || customer?.lastName,
          id: OTHER,
        }
      }
    } else {
      currentAddress = customerAddresses?.find(({ id }) => id === primaryAddressId)
    }

    if (currentAddress) {
      setValue('fulfillment.customerAddressId', currentAddress.id)
      FIELDS.map((field) => {
        let value
        if (field === 'address1') {
          value = currentAddress.address1
        } else {
          value = currentAddress[field]
        }
        setValue(`${nameGroupPrefix}[${field}]`, value)
      })
    }
  }, [customerAddresses, fulfillment, nameGroupPrefix, primaryAddressId, setValue, customer])

  return (
    <>
      {customer ? (
        <div className="mb-5">
          <label htmlFor={`${cssPrefix}-customer-address`}>Saved addresses</label>
          <select
            ref={register}
            className="custom-select form-control"
            disabled={!primaryAddressId}
            id={`${cssPrefix}-customer-address`}
            name="fulfillment.customerAddressId"
            onChange={handleSelect}
          >
            {customerAddresses?.map((address) => (
              <option key={address.id} value={address.id}>
                {customerAddressTitle(address)}
              </option>
            ))}
            <option value={OTHER}>Other...</option>
          </select>
        </div>
      ) : null}
      <AddressFields calcTransportCost={calcTransportCost} cssPrefix={cssPrefix} nameGroupPrefix={nameGroupPrefix} />
    </>
  )
}

export default memo(FulfillmentAddress)
